import { useEffect, useState } from "react";
import { Lock, Check, Crown, MousePointerClick, UserPlus, Link2, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { UserAvatar } from "@/components/UserBits";

export function InviteTiers({ g }) {
  return (
    <section className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 shadow-sm" data-testid="invite-tiers">
      <h3 className="font-bold text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-rose-500" /> Deine Einlade-Stufen</h3>
      <p className="text-xs text-slate-500 mt-1">Jede Stufe bringt Bonuspunkte, ein Abzeichen und einen eigenen Profilrahmen. Du hast <b className="text-rose-600">{g.count}</b> {g.count === 1 ? "Freund" : "Freunde"} eingeladen.</p>
      <ol className="mt-4 -mx-1 px-1 flex sm:grid sm:grid-cols-5 gap-2 sm:gap-3 overflow-x-auto no-scrollbar snap-x pb-1">
        {g.tiers.map((t) => (
          <li key={t.n} data-testid={`invite-tier-${t.n}`} data-reached={t.reached || undefined} className={`relative shrink-0 w-[6.2rem] sm:w-auto snap-start flex flex-col items-center text-center gap-1.5 rounded-2xl p-2.5 sm:p-3 border ${t.reached ? "border-rose-300 bg-rose-50" : "border-pink-100"}`}>
            <span className="relative">
              <span className={`block w-11 h-11 sm:w-14 sm:h-14 rounded-full p-[3px] lvl-frame lvl-frame-${t.frame} ${t.reached ? "" : "grayscale opacity-60"}`}>
                <span className="w-full h-full rounded-full bg-white flex items-center justify-center font-display font-black text-rose-600 text-sm sm:text-base">{t.n}</span>
              </span>
              <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ring-2 ring-white flex items-center justify-center ${t.reached ? "bg-emerald-500 text-white" : "bg-slate-700 text-white"}`}>{t.reached ? <Check className="w-3 h-3" strokeWidth={3} /> : <Lock className="w-2.5 h-2.5" />}</span>
            </span>
            <span className="text-[10px] sm:text-xs font-bold text-slate-700 leading-tight">{t.title}</span>
            <span className="text-[10px] sm:text-[11px] font-extrabold text-amber-600">+{t.points}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl bg-rose-50/70 border border-rose-100 p-3 sm:p-4">
    <Icon className="w-5 h-5 text-rose-500" />
    <p className="font-display font-extrabold text-xl sm:text-2xl mt-1" data-testid={testId}>{value.toLocaleString("de-DE")}</p>
    <p className="text-[11px] font-semibold text-slate-500 leading-tight">{label}</p>
  </div>
);

const KIND = { invite: "Einladung", post: "Beitrag", profile: "Profil", game: "Spiel" };

export function ShareStats({ g }) {
  const s = g.stats;
  return (
    <section className="min-w-0 bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 shadow-sm" data-testid="share-stats">
      <h3 className="font-bold text-lg flex items-center gap-2"><MousePointerClick className="w-5 h-5 text-rose-500" /> Deine Teilen-Statistik</h3>
      <p className="text-xs text-slate-500 mt-1">So viele Menschen haben deine geteilten Links geöffnet – und so viele sind dadurch Mitglied geworden.</p>
      <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
        <Stat icon={Link2} label="Geteilte Links" value={s.links} testId="share-stats-links" />
        <Stat icon={MousePointerClick} label="Aufrufe" value={s.clicks} testId="share-stats-clicks" />
        <Stat icon={UserPlus} label="Neue Mitglieder" value={s.signups} testId="share-stats-signups" />
      </div>
      {s.top_links.length > 0 && (
        <ul className="mt-4 divide-y divide-pink-50" data-testid="share-stats-list">
          {s.top_links.map((l) => (
            <li key={l.code} className="py-2.5 flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="shrink-0 text-[10px] font-extrabold uppercase tracking-wider text-rose-500 bg-rose-50 rounded-full px-2 py-0.5">{KIND[l.kind] || l.kind}</span>
              <span className="flex-1 min-w-0 basis-40 truncate text-slate-700">{l.title}</span>
              <span className="shrink-0 text-xs text-slate-500">{l.clicks} Aufrufe · <b className="text-emerald-600">{l.signups} neu</b></span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function TopInviters() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/growth/top").then(({ data }) => setD(data)).catch(() => setD({ top: [] })); }, []);
  if (!d) return null;
  const month = d.month ? new Date(`${d.month}-01T12:00:00`).toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : "";
  return (
    <section className="min-w-0 relative overflow-hidden rounded-3xl p-5 sm:p-6 bg-[#1f0a14] text-white shadow-xl" data-testid="top-inviters">
      <span className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-amber-400/10" aria-hidden />
      <h3 className="relative font-bold text-lg flex items-center gap-2"><Crown className="w-5 h-5 text-amber-300" fill="currentColor" /> Top-Werber im {month}</h3>
      <p className="relative mt-1 text-xs text-white/75" data-testid="top-inviters-prizes">Am Monatsende: Platz 1 <b className="text-amber-200">+{d.prizes?.[0] ?? 500}</b> & Krone 👑 · Platz 2 <b className="text-amber-200">+{d.prizes?.[1] ?? 250}</b> · Platz 3 <b className="text-amber-200">+{d.prizes?.[2] ?? 100}</b></p>
      {d.last_winner && <p className="relative mt-1 text-xs text-amber-200/90" data-testid="top-inviters-last">Letzter Monat gekrönt: <b>{d.last_winner.name}</b> mit {d.last_winner.count} {d.last_winner.count === 1 ? "Freund" : "Freunden"} 👑</p>}
      <ol className="relative mt-4 space-y-2" data-testid="top-inviters-list">
        {d.top.length === 0 && <li className="text-sm text-white/70 py-4 text-center">Diesen Monat noch frei – sei die oder der Erste! 🚀</li>}
        {d.top.map((r) => (
          <li key={r.user_id} data-testid={`top-inviter-${r.rank}`} className={`flex items-center gap-3 rounded-2xl px-3 py-2 ${r.rank === 1 ? "bg-gradient-to-r from-amber-400/25 to-transparent ring-1 ring-amber-300/40" : "bg-white/5"}`}>
            <span className={`w-7 text-center font-display font-black ${r.rank === 1 ? "text-amber-300" : "text-white/60"}`}>{r.rank === 1 ? <Crown className="w-5 h-5 mx-auto" fill="currentColor" /> : r.rank}</span>
            <UserAvatar userId={r.user_id} name={r.name} picture={r.picture} size="w-9 h-9" text="text-xs" noStory />
            <Link to={`/u/${r.user_id}`} className="flex-1 min-w-0 truncate font-semibold text-white hover:underline">{r.name}</Link>
            <span className="shrink-0 text-sm font-extrabold text-amber-200">{r.count} <span className="text-[11px] font-semibold text-white/60">{r.count === 1 ? "Freund" : "Freunde"}</span></span>
          </li>
        ))}
      </ol>
    </section>
  );
}
